import { api, AIOPS_API_BASE_URL, getOrgId } from "../../../api";

// Client Event Stream Class for SSE
class ScanStream {
  constructor() {
    this.callbacks = {};
    this.isClosed = false;
  }

  on(event, cb) {
    if (!this.callbacks[event]) {
      this.callbacks[event] = [];
    }
    this.callbacks[event].push(cb);
    return this;
  }

  emit(event, data) {
    if (this.isClosed) return;
    if (this.callbacks[event]) {
      this.callbacks[event].forEach((cb) => cb(data));
    }
  }

  close() {
    this.emit("close");
    this.isClosed = true;
  }
}

export class DiscoveryService {
  constructor(useMocks = false) {
    // We ignore useMocks now as mock data has been completely removed
    this.useMocks = false;
  }

  // Fetch all monitored subnets
  async fetchSubnets() {
    const data = await api("/discovery/subnets");
    if (Array.isArray(data)) {
      return data.map((item) => ({
        ...item,
        subnet: item.subnet_cidr, // Map backend subnet_cidr to frontend subnet field
        status: item.is_active ? "Active" : "Inactive",
        lastScan: item.last_scan_at ? new Date(item.last_scan_at).toLocaleString() : "Never",
      }));
    }
    return data;
  }

  // Onboard new subnet CIDR
  async addSubnet(subnetData) {
    const payload = {
      subnet_cidr: subnetData.subnet || subnetData.subnet_cidr,
      name: subnetData.name,
      relay_ip: subnetData.relay_ip,
      floor: subnetData.floor || "Floor 1",
      snmp_community: subnetData.snmp_community || "public",
      snmp_port: parseInt(subnetData.snmp_port || 161, 10),
      ssh_port: subnetData.ssh_port,
      ssh_user: subnetData.ssh_user,
      ssh_pkey: subnetData.ssh_pkey,
      scan_interval_secs: subnetData.scan_interval_secs,
      kafka_broker: subnetData.kafka_broker,
    };

    return await api("/discovery/subnets", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // Fetch discovered staging assets
  async fetchAssets() {
    const data = await api("/discovery/assets");
    if (Array.isArray(data)) {
      return data.map((item) => {
        const ports = (item.open_ports && item.open_ports.length > 0) ? item.open_ports : [22];
        const category = item.asset_category || item.type || "server";
        return {
          ...item,
          asset_category: category,
          subnet_id: item.subnet_id,
          ip: item.ip_address,
          name: item.hostname && item.hostname !== "no-hostname" ? item.hostname : item.ip_address,
          openPorts: ports,
          type: category,
          sshReachable: (ports.includes(22) || ports.length > 0) ? "Success" : "Unreachable",
          lastDeployStatus: item.lastDeployStatus || null, // Preserve deploy status
        };
      });
    }
    return data;
  }

  // Test target host credentials
  async testConnection(assetId) {
    return await api("/discovery/assets/test-connection", {
      method: "POST",
      body: JSON.stringify({ assetId }),
    });
  }

  // Test Relay SSH connection
  async testRelayConnection(relayData) {
    return await api("/discovery/subnets/bootstrap", {
      method: "POST",
      body: JSON.stringify(relayData),
    });
  }

  // Run Subnet Scan sweep (Streams logs via SSE)
  runDiscoveryScan(subnetId) {
    const stream = new ScanStream();
    const token = localStorage.getItem("aiops_token");
    const orgId = getOrgId();
    const eventSource = new EventSource(
      `${AIOPS_API_BASE_URL}/discovery/scan/${subnetId}/stream?token=${token}&org_id=${orgId}`
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "log") {
          stream.emit("log", data.log);
        } else if (data.type === "status") {
          stream.emit("status", data.status);
          if (data.status === "completed" || data.status === "failed") {
            eventSource.close();
            stream.emit("complete", { subnetId, timestamp: new Date().toISOString() });
            stream.close();
          }
        }
      } catch {
        stream.emit("log", event.data);
      }
    };

    eventSource.onerror = (err) => {
      if (stream.isClosed) return;
      if (eventSource.readyState === EventSource.CLOSED || eventSource.readyState === 2) {
        stream.close();
        return;
      }
      stream.emit("error", "SSE scan stream connection failed.");
      eventSource.close();
      stream.close();
    };

    stream.on("close", () => {
      eventSource.close();
    });

    return stream;
  }

  // Run Subnet Relay Bootstrap (Streams logs via SSE)
  bootstrapSubnet(subnetId) {
    const stream = new ScanStream();
    const token = localStorage.getItem("aiops_token");
    const orgId = getOrgId();
    const eventSource = new EventSource(
      `${AIOPS_API_BASE_URL}/discovery/subnets/${subnetId}/bootstrap/stream?token=${token}&org_id=${orgId}`
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "log") {
          stream.emit("log", data.log);
        } else if (data.type === "status") {
          stream.emit("status", data.status);
          if (data.status === "completed" || data.status === "failed") {
            eventSource.close();
            stream.emit("complete", { subnetId, timestamp: new Date().toISOString() });
            stream.close();
          }
        }
      } catch {
        stream.emit("log", event.data);
      }
    };

    eventSource.onerror = (err) => {
      stream.emit("error", "SSE bootstrap stream connection failed.");
      eventSource.close();
      stream.close();
    };

    stream.on("close", () => {
      eventSource.close();
    });

    return stream;
  }

  // Trigger agent deployment on staging hosts
  async runAgentDeploy(assetIps, authorizedBy = "admin@company.com", isRedeploy = false) {
    return await api("/discovery/deploy", {
      method: "POST",
      body: JSON.stringify({
        assetIps,
        authorizedBy,
        isRedeploy,
      }),
    });
  }

  // Retrieve progress updates for active deployment job
  getJobProgress(jobId) {
    const stream = new ScanStream();
    const token = localStorage.getItem("aiops_token");
    const orgId = getOrgId();
    const eventSource = new EventSource(
      `${AIOPS_API_BASE_URL}/discovery/deploy/jobs/${jobId}/stream?token=${token}&org_id=${orgId}`
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "log") {
          stream.emit("log", data.log);
        } else if (data.type === "progress") {
          stream.emit("progress", { progress: data.progress, completed: data.completed });
        } else if (data.type === "status") {
          stream.emit("status", data.status);
          if (data.status === "completed" || data.status === "failed" || data.status === "completed_with_errors") {
            eventSource.close();
            stream.emit("complete", { jobId, progress: 100 });
            stream.close();
          }
        }
      } catch {
        stream.emit("error", "Failed to parse SSE job progress payload.");
      }
    };

    eventSource.onerror = (err) => {
      stream.emit("error", "SSE deployment progress stream failed.");
      eventSource.close();
      stream.close();
    };

    stream.on("close", () => {
      eventSource.close();
    });

    return stream;
  }

  // Delete a monitored subnet
  async deleteSubnet(subnetId) {
    return await api(`/discovery/subnets/${subnetId}`, {
      method: "DELETE",
    });
  }

  // Fetch metadata options (types, statuses)
  async fetchMeta() {
    return await api("/discovery/meta");
  }
}

// ==========================================
// SERVICE SINGLETON EXPORTS
// ==========================================
export const discoveryService = new DiscoveryService(false);

export const fetchSubnets = () => discoveryService.fetchSubnets();
export const addSubnet = (data) => discoveryService.addSubnet(data);
export const fetchAssets = () => discoveryService.fetchAssets();
export const testConnection = (ip) => discoveryService.testConnection(ip);
export const testRelayConnection = (data) => discoveryService.testRelayConnection(data);
export const runDiscoveryScan = (id) => discoveryService.runDiscoveryScan(id);
export const runAgentDeploy = (ips, authorizedBy, isRedeploy) => discoveryService.runAgentDeploy(ips, authorizedBy, isRedeploy);
export const getJobProgress = (id) => discoveryService.getJobProgress(id);
export const bootstrapSubnet = (id) => discoveryService.bootstrapSubnet(id);
export const deleteSubnet = (id) => discoveryService.deleteSubnet(id);
export const fetchMeta = () => discoveryService.fetchMeta();
